'use client';

import React from 'react';

import { useGlobal } from '@/contexts/GlobalContext';
import { useModal } from '@/contexts/ModalContext';
import { PrivateAicon } from '@/components/common/Aicons';

const HintModal: React.FC = () => {
  /**
   * context & hooks
   */
  const context = useGlobal();
  const { closeModal } = useModal();

  const handleModalClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // モーダルの背景をクリックした場合にだけ closeModal を呼び出す
    const modalBackground = document.getElementById('modal-background');
    if (e.target === modalBackground) {
      closeModal();
    }
  };

  return (
    <div
      id="modal-background"
      className="fixed inset-0 z-30 w-full flex items-center justify-center overflow-x-hidden overflow-y-auto outline-none focus:outline-none"
      onClick={handleModalClick}>
      <div className="relative w-11/12 sm:w-6/12 max-h-[85dvh] overflow-y-auto rounded-lg">
        <div className="relative flex flex-col bg-white border-0 rounded-lg outline-none focus:outline-none">
          <div
            className="flex justify-between items-center border-b border-green-500 px-4 py-2 bg-white sticky top-0 z-10 rounded-t-lg"
            style={{ borderBottom: `solid 1px ${context.team?.color_a}` }}>
            <h3 className="flex items-center font-footballlog italic text-sm font-semibold text-gray-500 dark:text-white">
              サッカーノートのヒント
            </h3>
            <button
              type="button"
              onClick={closeModal}
              className="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-gray-600 dark:hover:text-white">
              <svg
                className="w-3 h-3"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14">
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span className="sr-only">Close menu</span>
            </button>
          </div>
          <div className="relative p-4">
            <div className="bg-gray-100 p-4 rounded-lg">
              <h4 className="flex items-center font-footballlog italic font-semibold text-gray-200 text-5xl">
                <svg
                  className="w-8 h-8"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="20"
                  fill="none"
                  viewBox="0 0 14 20">
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 7a3 3 0 0 1 3-3M5 19h4m0-3c0-4.1 4-4.9 4-9A6 6 0 1 0 1 7c0 4 4 5 4 9h4Z"
                  />
                </svg>
                Hints
              </h4>
              <ul className="list my-4 space-y-4 text-gray-600 text-sm">
                <li>
                  <div className="flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3.5 h-3.5 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>サッカーの振り返りをGood・Bad・Nextの観点で記録しましょう。</span>
                  </div>
                </li>
                <li>
                  <div className="flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3.5 h-3.5 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>
                      サッカーノートの内容はサッカーに関することならなんでもOK。どんどん言語化していきましょう。
                    </span>
                  </div>
                  <ul className="ps-5 mt-2 space-y-2 list-disc list-inside">
                    <li>トレーニングの振り返り</li>
                    <li>試合の振り返り</li>
                    <li>試合観戦の振り返り</li>
                    <li>指導の振り返り&nbsp;&nbsp;etc...</li>
                  </ul>
                </li>
                <li className="flex items-top space-x-3 rtl:space-x-reverse">
                  <svg
                    className="mt-1 flex-shrink-0 w-3.5 h-3.5 text-green-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 16 12">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M1 5.917 5.724 10.5 15 1.5"
                    />
                  </svg>
                  <span>
                    <svg
                      className="float-left w-3 h-3 mt-1 me-1"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 17 10">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeWidth="2"
                        d="M6 1h10M6 5h10M6 9h10M1.49 1h.01m-.01 4h.01m-.01 4h.01"
                      />
                    </svg>
                    アイコンよりリストを追加して、箇条書きでシンプルに記録することができます。
                  </span>
                </li>
                <li>
                  <div className="flex items-top space-x-3 rtl:space-x-reverse">
                    <svg
                      className="mt-1 flex-shrink-0 w-3.5 h-3.5 text-green-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 16 12">
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M1 5.917 5.724 10.5 15 1.5"
                      />
                    </svg>
                    <span>
                      <svg
                        className="float-left w-3 h-3 mt-1 me-1"
                        aria-hidden="true"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 18 18">
                        <path
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 14h6m-3 3v-6M1.857 1h4.286c.473 0 .857.384.857.857v4.286A.857.857 0 0 1 6.143 7H1.857A.857.857 0 0 1 1 6.143V1.857C1 1.384 1.384 1 1.857 1Zm10 0h4.286c.473 0 .857.384.857.857v4.286a.857.857 0 0 1-.857.857h-4.286A.857.857 0 0 1 11 6.143V1.857c0-.473.384-.857.857-.857Zm-10 10h4.286c.473 0 .857.384.857.857v4.286a.857.857 0 0 1-.857.857H1.857A.857.857 0 0 1 1 16.143v-4.286c0-.473.384-.857.857-.857Z"
                        />
                      </svg>
                      アイコンからノートのカスタマイズができます。
                    </span>
                  </div>
                  <ul className="ps-5 mt-2 space-y-1 list-disc list-inside">
                    <li className="my-2">
                      ex. ゲームのスコアを記録したい。
                      <ul className="ps-5 mt-2 space-y-1 list-inside">
                        <li className="my-2">→ 「Score（ゲームスコア）」をON</li>
                      </ul>
                    </li>
                    <li className="my-2">
                      ex. プライベートなメモを記録したい。
                      <ul className="ps-5 mt-2 space-y-1 list-inside">
                        <li className="my-2">→ 「Memo（自由記入）」をON</li>
                      </ul>
                    </li>
                  </ul>
                </li>
                <li className="flex items-top space-x-3 rtl:space-x-reverse">
                  <svg
                    className="mt-1 flex-shrink-0 w-3.5 h-3.5 text-green-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 16 12">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M1 5.917 5.724 10.5 15 1.5"
                    />
                  </svg>
                  <span className="">
                    <span className="float-left pt-0.5">
                      <PrivateAicon />
                    </span>
                    タグがついている項目は共有されません。
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HintModal;
